import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { resolveAuthenticatedEntryPath } from '../../lib/adminAccess';

const configuredAdminEmails = String(
  import.meta.env.VITE_AERO_ADMIN_EMAILS ?? import.meta.env.VITE_PASSPORT_ADMIN_EMAILS ?? '',
);

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawNext = params.get('next') ?? '';
    return rawNext.startsWith('/') ? rawNext : '/app/dashboard';
  }, [location.search]);
  const isFlightFollowIntent = useMemo(
    () => nextPath.includes('/passport/shared/') && nextPath.includes('follow=flights'),
    [nextPath],
  );

  useEffect(() => {
    setMessage(
      isFlightFollowIntent ? t('auth.callback.flightFollow') : t('auth.callback.default'),
    );
  }, [isFlightFollowIntent, t]);

  useEffect(() => {
    const handleAuth = async () => {
      if (!supabase) {
        navigate(`/app/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
        return;
      }

      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);

          if (error) {
            console.error('Erro ao trocar code por sessão:', error);
            navigate(`/app/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
            return;
          }
        }

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          navigate(resolveAuthenticatedEntryPath(session.user, nextPath, configuredAdminEmails), { replace: true });
        } else {
          navigate(`/app/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
        }
      } catch (err) {
        console.error('Erro no callback de auth:', err);
        navigate(`/app/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
      }
    };

    handleAuth();
  }, [navigate, nextPath]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1628] to-[#0F1F38] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-md text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#C9A84C]/15 flex items-center justify-center">
          <i className="ri-loader-4-line text-2xl text-[#C9A84C] animate-spin"></i>
        </div>
        <h1 className="text-2xl font-extrabold text-white mb-2">
          {isFlightFollowIntent ? t('auth.callback.flightFollowTitle') : t('auth.callback.title')}
        </h1>
        <p className="text-white/86 text-sm">{message}</p>
      </div>
    </div>
  );
}
